import { gql, type Variables } from 'graphql-request';

import { getQueryClient } from '@/api/queryClient';
import logToDatadog from '@/logger/logToDatadog';
import gqlRequest from '@/utils/gqlRequest';

import {
  getCartIdCookie,
  invalidateCartData,
  saveCartIdCookie,
} from '../utils/cartUtils';
import type {
  ChangeWarranty,
  ChangeWarranty_cart_changeWarranty,
  ChangeWarrantyVariables,
} from './types/ChangeWarranty';

export const query = gql`
  query ChangeWarranty($cartId: String, $skuCode: String) {
    cart(cartId: $cartId) {
      changeWarranty(warrantySkuCode: $skuCode) {
        cartId
        resultCategory
        errorCode
        invalidReason
      }
    }
  }
`;

export function withQueryKey(req: ChangeWarrantyVariables) {
  return {
    queryKey: ['changeWarranty', req.cartId],
    queryFn: async (): Promise<ChangeWarranty_cart_changeWarranty> => {
      try {
        const response = await gqlRequest<ChangeWarranty | undefined>(
          query,
          req as unknown as Variables,
        );
        const result = response?.cart?.changeWarranty;

        if (result?.resultCategory !== 'success')
          throw new Error('ChangeWarranty failed');

        if (result.cartId) {
          saveCartIdCookie(result.cartId);
        }

        invalidateCartData();

        return result;
      } catch (error) {
        logToDatadog({
          error,
          message: 'ChangeWarranty query failed',
          module: 'cart',
        });
        throw error;
      }
    },
  };
}

export async function changeWarranty(
  skuCode: string,
): Promise<ChangeWarranty_cart_changeWarranty> {
  const cartId = getCartIdCookie();
  const queryClient = getQueryClient();

  return queryClient.fetchQuery({
    ...withQueryKey({ cartId, skuCode: skuCode || null }),
  });
}
